import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { usePreferences } from './usePreferences'
import { checkLimit, incrementUsage } from '../lib/usageLimits'
import type { SuggestedRecipe, SuggestionResponse } from '../lib/recipeTypes'

type State = {
  suggestions:   SuggestedRecipe[]
  loading:       boolean
  error:         string | null
  cached:        boolean
  expiringCount: number
}

export function useRecipeSuggestions(): State & { refresh: () => void } {
  const { user }    = useAuth()
  const { isPremium } = usePreferences()
  const [state,      setState]      = useState<State>({
    suggestions: [], loading: true, error: null, cached: false, expiringCount: 0,
  })
  const [refreshKey, setRefreshKey] = useState(0)

  const { data: householdId } = useQuery({
    queryKey:  ['householdId', user?.id],
    enabled:   !!user?.id,
    staleTime: 60_000,
    queryFn:   async () => {
      const { data } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user!.id)
        .maybeSingle()
      return (data?.household_id as string) ?? null
    },
  })

  const { data: pantryData } = useQuery({
    queryKey:  ['recipe-suggestions-pantry', householdId],
    enabled:   !!householdId,
    staleTime: 30_000,
    queryFn:   async () => {
      const today  = new Date(); today.setHours(0, 0, 0, 0)
      const in7    = new Date(today); in7.setDate(in7.getDate() + 7)
      const past2  = new Date(today); past2.setDate(past2.getDate() - 2)
      const in7Str   = in7.toISOString().split('T')[0]
      const past2Str = past2.toISOString().split('T')[0]

      const { data } = await supabase
        .from('pantry_items')
        .select('name, track_expiration, current_expiration_date, current_quantity')
        .eq('household_id', householdId!)
        .eq('is_active', true)

      const items = data ?? []

      const expiringItems = items
        .filter(i => {
          if (!i.track_expiration || !i.current_expiration_date) return false
          return i.current_expiration_date >= past2Str && i.current_expiration_date <= in7Str
        })
        .map(i => {
          const expDate        = new Date(i.current_expiration_date + 'T00:00:00')
          const daysUntilExpiry = Math.floor((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
          return {
            name:              i.name,
            days_until_expiry: Math.max(0, daysUntilExpiry),
            qty:               i.current_quantity ?? null,
          }
        })

      const onHandNames = items.map(i => i.name)
      return { expiringItems, onHandNames }
    },
  })

  const expiringHash = useMemo(
    () => (pantryData?.expiringItems ?? [])
      .map(i => `${i.name}:${i.days_until_expiry}`)
      .sort()
      .join('|'),
    [pantryData?.expiringItems],
  )

  useEffect(() => {
    if (!householdId || !pantryData) return
    const data = pantryData  // capture so the async closure always has a non-undefined ref
    let cancelled = false

    async function run() {
      setState(s => ({ ...s, loading: true, error: null, expiringCount: data.expiringItems.length }))

      if (data.expiringItems.length === 0) {
        if (!cancelled) {
          setState({ suggestions: [], loading: false, error: null, cached: false, expiringCount: 0 })
        }
        return
      }

      const { data: sessionData } = await supabase.auth.getSession()
      const jwt = sessionData?.session?.access_token
      if (!jwt) {
        if (!cancelled) {
          setState(s => ({ ...s, loading: false, error: 'Not signed in' }))
        }
        return
      }

      // Free-tier monthly cap on AI suggestions. checkLimit() resets the
      // counter if it's a new calendar month; returns null for premium so
      // the gate is a no-op.
      const limitResult = await checkLimit(user?.id ?? null, isPremium, 'ai_suggestions')
      if (limitResult?.hitLimit) {
        if (!cancelled) {
          setState(s => ({
            ...s,
            suggestions: [],
            loading:     false,
            error:       'limit_reached',
          }))
        }
        return
      }

      try {
        const res = await fetch('/api/claude-suggestions', {
          method:  'POST',
          headers: {
            Authorization:  `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            household_id:   householdId,
            expiring_items: data.expiringItems,
            on_hand:        data.onHandNames,
            force_refresh:  refreshKey > 0,
          }),
        })

        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const raw: SuggestionResponse = normalize(await res.json())

        // Cached responses don't fire a Claude call — don't burn a count.
        if (!raw.cached) {
          await incrementUsage(user?.id ?? null, isPremium, 'ai_suggestions')
        }

        if (!cancelled) {
          setState(s => ({
            ...s,
            suggestions: raw.suggestions,
            loading:     false,
            error:       null,
            cached:      raw.cached,
          }))
        }
      } catch (err) {
        if (!cancelled) {
          setState(s => ({
            ...s,
            suggestions: [],
            loading:     false,
            error:       (err as Error).message,
          }))
        }
      }
    }

    run()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId, expiringHash, refreshKey])

  return { ...state, refresh: () => setRefreshKey(k => k + 1) }
}

function normalize(raw: any): SuggestionResponse {
  return {
    cached:      !!raw.cached,
    generatedAt: raw.generated_at ?? new Date().toISOString(),
    suggestions: (raw.suggestions ?? [])
      .map((s: any): SuggestedRecipe => ({
        id:           s.id,
        name:         s.name,
        why:          s.why,
        usesExpiring: s.uses_expiring ?? [],
        usesOnHand:   s.uses_on_hand  ?? [],
        needsToBuy:   s.needs_to_buy  ?? [],
        prepMinutes:  s.prep_minutes  ?? 0,
        serves:       s.serves        ?? 0,
        rank:         s.rank          ?? 999,
      }))
      .sort((a: SuggestedRecipe, b: SuggestedRecipe) => a.rank - b.rank),
  }
}
